import { Typography } from '@material-ui/core'
import { withStyles, WithStyles } from '@material-ui/core/styles'
import createStyles from '@material-ui/core/styles/createStyles'
import classNames from 'classnames'
import React, { ReactNode } from 'react'
import {
  Column,
  ColumnProps,
  Index,
  Table,
  TableCellDataGetter,
  TableCellRenderer,
  TableHeaderRowProps,
  TableRowProps,
} from 'react-virtualized'

import 'react-virtualized/styles.css'
import { MAX_FETCH_ITEMS } from 'src/api/base-api'

import { TABLE_HEADER_HEIGHT, TABLE_ROW_HEIGHT } from '.'

const styles = () =>
  createStyles({
    header: {
      height: TABLE_HEADER_HEIGHT,
    },
    row: {
      height: TABLE_ROW_HEIGHT,
    },
    table: {
      overflowX: 'auto',
    },
    rowEven: {
      backgroundClip: 'padding-box',
      background: '#f4f9ff',
    },
    rowOdd: {
      backgroundClip: 'padding-box',
      background: '#FFF',
    },
    column: {
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-start',
    },
    columnHeader: {
      display: 'flex',
      justifyContent: 'flex-start',
      alignItems: 'center',
    },
  })
export interface IColumnProps extends ColumnProps {
  cellDataGetter?: TableCellDataGetter
  cellRenderer?: TableCellRenderer
  dataKey: string
  label: string
  width: number
}

interface ITableProps {
  columnProps: IColumnProps[]
  endRowRenderer?: (rowRendererProps: TableRowProps) => ReactNode
  endRowRendererWidth?: number
  headerHeight: number
  height: number
  items: any
  rowHeight: number | ((index: Index) => number)
  rowRenderer?: (rowRendererProps: TableRowProps) => JSX.Element
  totalCount: number
  width: number
}

const columnRenderer = (columnsProps: IColumnProps[], className: string, headerClassName: string) => {
  return columnsProps.map((colProp: IColumnProps, idx: number) => (
    <Column
      {...colProp}
      label={colProp.label}
      minWidth={colProp.width}
      flexGrow={1}
      dataKey={colProp.dataKey}
      key={idx}
      cellRenderer={colProp.cellRenderer}
      cellDataGetter={colProp.cellDataGetter}
      className={className}
      headerClassName={headerClassName}
      style={{ alignItems: 'flex-start', paddingTop: '1em' }}
    />
  ))
}

const VirtualizedTable = (props: WithStyles<typeof styles> & ITableProps) => {
  const { headerHeight, rowHeight, items, columnProps, classes, width, height, totalCount } = props
  const columns = columnRenderer(columnProps, classes.column, classes.columnHeader)
  const cache: any[] = items

  const isRowLoaded = ({ index }: { index: number }) => {
    return !!cache[index]
  }

  const rowGetter = ({ index }: { index: number }) => {
    if (isRowLoaded({ index })) {
      return cache[index]
    }
    // see: https://github.com/bvaughn/react-virtualized/issues/1012#issuecomment-373166952
    return {}
  }

  const headerRowRenderer = (headerRendererProps: TableHeaderRowProps) => {
    return (
      <div
        className={classNames(headerRendererProps.className, classes.header, classes.row)}
        style={{ ...headerRendererProps.style }}
      >
        {headerRendererProps.columns}
        {props.endRowRendererWidth && (
          <span style={{ paddingRight: props.endRowRendererWidth, paddingTop: props.endRowRendererWidth }} />
        )}
      </div>
    )
  }

  const rowRenderer = (rowRendererProps: TableRowProps) => {
    const rowClass = rowRendererProps.index % 2 ? classes.rowEven : classes.rowOdd

    return (
      <div
        className={classNames(rowRendererProps.className, classes.row, rowClass)}
        key={rowRendererProps.key}
        style={{ ...rowRendererProps.style }}
      >
        {rowRendererProps.columns}
        {props.endRowRenderer?.(rowRendererProps)}
      </div>
    )
  }

  const tableProps = {
    width,
    height,
    headerHeight,
    rowHeight,
    rowCount: Math.min(totalCount, MAX_FETCH_ITEMS),
    rowGetter,
    headerRowRenderer,
    rowRenderer: props.rowRenderer ?? rowRenderer,
    className: classes.table,
    headerClassname: classes.header,
  }

  return (
    <Typography component="div" variant="body1">
      <Table {...tableProps}>{columns}</Table>
    </Typography>
  )
}

export default withStyles(styles)(VirtualizedTable)
